import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Ban, Clock, Mail, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

const BannedUserScreen = ({ banInfo, onLogout, onAppealSubmitted }) => {
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealReason, setAppealReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [appealSubmitted, setAppealSubmitted] = useState(banInfo?.has_pending_appeal || false);
  const [appealStatus, setAppealStatus] = useState(banInfo?.appeal_status || null);
  const [error, setError] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'Permanent';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSubmitAppeal = async () => {
    if (!appealReason.trim()) {
      setError('Please provide a reason for your appeal');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/user/appeal`, {
        reason: appealReason,
        additional_info: additionalInfo || null
      }, {
        withCredentials: true
      });

      if (response.data.appeal_id) {
        setAppealSubmitted(true);
        setAppealStatus('pending');
        setShowAppealModal(false);
        if (onAppealSubmitted) {
          onAppealSubmitted(response.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit appeal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getAppealStatusDisplay = () => {
    switch (appealStatus) {
      case 'pending':
        return (
          <div className="flex items-center gap-2 text-yellow-400">
            <Clock className="h-5 w-5" />
            <span>Appeal Pending Review</span>
          </div>
        );
      case 'under_review':
        return (
          <div className="flex items-center gap-2 text-blue-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Appeal Under Review</span>
          </div>
        );
      case 'approved':
        return (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span>Appeal Approved</span>
          </div>
        );
      case 'denied':
        return (
          <div className="flex items-center gap-2 text-red-400">
            <XCircle className="h-5 w-5" />
            <span>Appeal Denied</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-red-950/20 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Ban Card */}
        <div className="bg-slate-800/90 border border-red-500/50 rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-red-900/50 p-6 text-center border-b border-red-500/30">
            <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <Ban className="h-10 w-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Account Suspended</h1>
            <p className="text-red-200/80 text-sm">Your account has been suspended for violating our Community Guidelines</p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Ban Details */}
            <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Suspension Type:</span>
                <span className={`font-medium ${banInfo?.suspension_type === 'permanent' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {banInfo?.suspension_type === 'permanent' ? 'Permanent' : 'Temporary'}
                </span>
              </div>
              
              {banInfo?.suspension_type !== 'permanent' && banInfo?.suspension_end && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Ends:</span>
                  <span className="text-white font-medium">{formatDate(banInfo.suspension_end)}</span>
                </div>
              )}
              
              <div className="pt-2 border-t border-slate-700">
                <span className="text-gray-400 text-sm block mb-1">Reason:</span>
                <p className="text-white">{banInfo?.suspension_reason || 'Violation of Community Guidelines'}</p>
              </div>
            </div>

            {/* Appeal Status */}
            {appealSubmitted && (
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="text-white font-medium mb-2">Appeal Status</h3>
                {getAppealStatusDisplay()}
                <p className="text-gray-400 text-sm mt-2">
                  {appealStatus === 'pending' && 'Your appeal has been submitted and is awaiting review. You will be notified once a decision is made.'}
                  {appealStatus === 'under_review' && 'Your appeal is currently being reviewed by our Trust & Safety team.'}
                  {appealStatus === 'denied' && 'Your appeal has been denied. If you believe this is in error, please contact appeals@gemslive.tv'}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-2">
              {!appealSubmitted && (
                <Button
                  onClick={() => setShowAppealModal(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Appeal This Decision
                </Button>
              )}
              
              <Button
                onClick={onLogout}
                variant="outline"
                className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                Sign Out
              </Button>
            </div>

            {/* Help Text */}
            <div className="text-center pt-4 border-t border-slate-700">
              <p className="text-gray-400 text-sm">
                Need help? Contact us at{' '}
                <a href="mailto:appeals@gemslive.tv" className="text-emerald-400 hover:underline">
                  appeals@gemslive.tv
                </a>
              </p>
              <a 
                href="/community-guidelines" 
                target="_blank"
                className="text-emerald-400 hover:underline text-sm"
              >
                View Community Guidelines
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Appeal Modal */}
      <Dialog open={showAppealModal} onOpenChange={setShowAppealModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Appeal Your Suspension
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-200 text-sm">
                Please provide a clear explanation of why you believe this suspension was made in error. 
                False or misleading appeals may result in extended suspensions.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Why do you believe this suspension should be lifted? *
              </label>
              <Textarea
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Explain your situation..."
                className="min-h-[100px]"
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">{appealReason.length}/1000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Additional Information (Optional)
              </label>
              <Textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Any additional context or evidence..."
                className="min-h-[80px]"
                maxLength={500}
              />
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setShowAppealModal(false)}
                variant="outline"
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAppeal}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting || !appealReason.trim()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Appeal
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Appeals are typically reviewed within 48-72 hours. You will receive an email notification when a decision is made.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannedUserScreen;
